import { Box, Divider, Typography, Link, Tooltip } from '@mui/material'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { useCustom } from '@refinedev/core'
import { Link as RouterLink } from 'react-router-dom'

import Loading from './Loading'
import ErrorMessage from './ErrorMessage'
import DatabaseStats from './DatabaseStats'
import OppTagStats from './OppTagStats'
import OrgActivityMatrix from './OrgActivityMatrix'

const linkSx = {
  textDecoration: 'none',
  color: 'inherit',
  fontWeight: 'inherit',
  '&:hover': {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
}

const bigLinkSx = {
  mt: 2,
  mb: 5,
  fontSize: '32px',
  fontWeight: '700',
  textDecoration: 'none',
  color: 'primary.main',
  display: 'block',
  textAlign: 'center',
  width: '100%',
  p: 1,
  borderRadius: 1,
  '&:hover': {
    textDecoration: 'underline',
    cursor: 'pointer',
    backgroundColor: 'action.hover',
  },
}

export default function Dashboard() {
  // Date calculations for upcoming funding rounds
  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(today.getDate() + 1)
  const oneMonthAway = new Date(today)
  oneMonthAway.setMonth(today.getMonth() + 1)

  const startDate = tomorrow.toISOString().split('T')[0]
  const endDate = oneMonthAway.toISOString().split('T')[0]

  // --- Funder counts ---
  const {
    data: totalFundersData,
    isLoading: totalFundersLoading,
    isError: totalFundersError,
  } = useCustom({
    url: `/funders/count`,
    method: 'get',
    errorNotification: false,
  })

  const {
    data: activeFundersData,
    isLoading: activeFundersLoading,
    isError: activeFundersError,
  } = useCustom({
    url: `/funders/count`,
    method: 'get',
    errorNotification: false,
    config: {
      query: {
        filters: JSON.stringify([
          { field: 'active', operator: 'eq', value: true },
        ]),
      },
    },
  })

  const {
    data: inactiveFundersData,
    isLoading: inactiveFundersLoading,
    isError: inactiveFundersError,
  } = useCustom({
    url: `/funders/count`,
    method: 'get',
    errorNotification: false,
    config: {
      query: {
        filters: JSON.stringify([
          { field: 'active', operator: 'eq', value: false },
        ]),
      },
    },
  })

  const {
    data: greenFundersData,
    isLoading: greenFundersLoading,
    isError: greenFundersError,
  } = useCustom({
    url: `/funders/count`,
    method: 'get',
    errorNotification: false,
    config: {
      query: {
        filters: JSON.stringify([
          { field: 'opps_status', operator: 'eq', value: 'green' },
        ]),
      },
    },
  })

  const {
    data: yellowFundersData,
    isLoading: yellowFundersLoading,
    isError: yellowFundersError,
  } = useCustom({
    url: `/funders/count`,
    method: 'get',
    errorNotification: false,
    config: {
      query: {
        filters: JSON.stringify([
          { field: 'opps_status', operator: 'eq', value: 'yellow' },
        ]),
      },
    },
  })

  // --- Opportunity counts ---
  const {
    data: totalOpportunitiesData,
    isLoading: totalOpportunitiesLoading,
    isError: totalOpportunitiesError,
  } = useCustom({
    url: `/opportunities/count`,
    method: 'get',
    config: {},
    errorNotification: false,
  })

  const {
    data: greenOpportunitiesData,
    isLoading: greenOpportunitiesLoading,
    isError: greenOpportunitiesError,
  } = useCustom({
    url: `/opportunities/count`,
    method: 'get',
    errorNotification: false,
    config: {
      query: {
        filters: JSON.stringify([
          { field: 'traffic_light_status', operator: 'eq', value: 'green' },
        ]),
      },
    },
  })

  const {
    data: yellowOpportunitiesData,
    isLoading: yellowOpportunitiesLoading,
    isError: yellowOpportunitiesError,
  } = useCustom({
    url: `/opportunities/count`,
    method: 'get',
    errorNotification: false,
    config: {
      query: {
        filters: JSON.stringify([
          { field: 'traffic_light_status', operator: 'eq', value: 'yellow' },
        ]),
      },
    },
  })

  const {
    data: redOpportunitiesData,
    isLoading: redOpportunitiesLoading,
    isError: redOpportunitiesError,
  } = useCustom({
    url: `/opportunities/count`,
    method: 'get',
    errorNotification: false,
    config: {
      query: {
        filters: JSON.stringify([
          { field: 'traffic_light_status', operator: 'eq', value: 'red' },
        ]),
      },
    },
  })

  const {
    data: needsWorkOpportunitiesData,
    isLoading: needsWorkOpportunitiesLoading,
    isError: needsWorkOpportunitiesError,
  } = useCustom({
    url: `/opportunities/count`,
    method: 'get',
    errorNotification: false,
    config: {
      query: {
        filters: JSON.stringify([
          { field: 'flag_for_updates', operator: 'eq', value: 'Needs Work' },
        ]),
      },
    },
  })

  const {
    data: alwaysOpenGreenData,
    isLoading: alwaysOpenGreenLoading,
    isError: alwaysOpenGreenError,
  } = useCustom({
    url: `/opportunities/count`,
    method: 'get',
    errorNotification: false,
    config: {
      query: {
        filters: JSON.stringify([
          { field: 'traffic_light_status', operator: 'eq', value: 'green' },
          { field: 'always_open', operator: 'eq', value: true },
        ]),
      },
    },
  })

  const {
    data: currentFundingRoundsData,
    isLoading: currentFundingRoundsLoading,
    isError: currentFundingRoundsError,
  } = useCustom({
    url: `/opportunities/count`,
    method: 'get',
    errorNotification: false,
    config: {
      query: {
        filters: JSON.stringify([
          { field: 'traffic_light_status', operator: 'eq', value: 'green' },
          {
            field: 'has_current_funding_rounds',
            operator: 'eq',
            value: 'true',
          },
          { field: 'always_open', operator: 'eq', value: false },
        ]),
      },
    },
  })

  const {
    data: upcomingFundingRoundsData,
    isLoading: upcomingFundingRoundsLoading,
    isError: upcomingFundingRoundsError,
  } = useCustom({
    url: `/opportunities/count`,
    method: 'get',
    errorNotification: false,
    config: {
      query: {
        filters: JSON.stringify([
          { field: 'traffic_light_status', operator: 'eq', value: 'green' },
          { field: 'application_open_date', operator: 'gte', value: startDate },
          { field: 'application_open_date', operator: 'lte', value: endDate },
        ]),
      },
    },
  })

  const {
    data: activeNoProfileData,
    isLoading: activeNoProfileLoading,
    isError: activeNoProfileError,
  } = useCustom({
    url: `/opportunities/count`,
    method: 'get',
    errorNotification: false,
    config: {
      query: {
        filters: JSON.stringify([
          { field: 'flag_for_updates', operator: 'eq', value: 'Active' },
          { field: 'profile_text', operator: 'null_or_empty', value: true },
        ]),
      },
    },
  })

  const {
    data: needsRegionsData,
    isLoading: needsRegionsLoading,
    isError: needsRegionsError,
  } = useCustom({
    url: `/opportunities/count`,
    method: 'get',
    errorNotification: false,
    config: {
      query: {
        filters: JSON.stringify([
          { field: 'region_id', operator: 'null', value: true },
          {
            field: 'inherit_regions_from_funder',
            operator: 'null',
            value: true,
          },
          { field: 'flag_for_updates', operator: 'eq', value: 'Active' },
        ]),
      },
    },
  })

  const {
    data: needsFundingRoundsData,
    isLoading: needsFundingRoundsLoading,
    isError: needsFundingRoundsError,
  } = useCustom({
    url: `/opportunities/count`,
    method: 'get',
    errorNotification: false,
    config: {
      query: {
        filters: JSON.stringify([
          { field: 'needs_funding_rounds', operator: 'eq', value: 'true' },
        ]),
      },
    },
  })

  const {
    data: expiredRoundsData,
    isLoading: expiredRoundsLoading,
    isError: expiredRoundsError,
  } = useCustom({
    url: `/opportunities/count`,
    method: 'get',
    errorNotification: false,
    config: {
      query: {
        filters: JSON.stringify([
          { field: 'has_expired_rounds', operator: 'eq', value: 'true' },
        ]),
      },
    },
  })

  const isLoading =
    totalFundersLoading ||
    activeFundersLoading ||
    inactiveFundersLoading ||
    greenFundersLoading ||
    yellowFundersLoading ||
    totalOpportunitiesLoading ||
    greenOpportunitiesLoading ||
    yellowOpportunitiesLoading ||
    redOpportunitiesLoading ||
    needsWorkOpportunitiesLoading ||
    alwaysOpenGreenLoading ||
    currentFundingRoundsLoading ||
    upcomingFundingRoundsLoading ||
    activeNoProfileLoading ||
    needsRegionsLoading ||
    needsFundingRoundsLoading ||
    expiredRoundsLoading

  const hasError =
    totalFundersError ||
    activeFundersError ||
    inactiveFundersError ||
    greenFundersError ||
    yellowFundersError ||
    totalOpportunitiesError ||
    greenOpportunitiesError ||
    yellowOpportunitiesError ||
    redOpportunitiesError ||
    needsWorkOpportunitiesError ||
    alwaysOpenGreenError ||
    currentFundingRoundsError ||
    upcomingFundingRoundsError ||
    activeNoProfileError ||
    needsRegionsError ||
    needsFundingRoundsError ||
    expiredRoundsError

  if (isLoading) return <Loading />
  if (hasError) return <ErrorMessage />

  const greenOpportunitiesCount = greenOpportunitiesData?.data?.count || 0
  const totalOpportunitiesCount = totalOpportunitiesData?.data?.count || 0
  const greenOpportunitiesPercentage = totalOpportunitiesCount
    ? ((greenOpportunitiesCount / totalOpportunitiesCount) * 100).toFixed(1)
    : 0
  const futureRoundsCount = Math.max(
    0,
    greenOpportunitiesCount -
      (alwaysOpenGreenData?.data?.count || 0) -
      (currentFundingRoundsData?.data?.count || 0) -
      (upcomingFundingRoundsData?.data?.count || 0),
  )
  const yellowTotal = yellowOpportunitiesData?.data?.count || 0
  const yellowOpportunitiesPercentage = totalOpportunitiesCount
    ? ((yellowTotal / totalOpportunitiesCount) * 100).toFixed(1)
    : 0
  const yellowSubTotal =
    (needsWorkOpportunitiesData?.data?.count || 0) +
    (activeNoProfileData?.data?.count || 0) +
    (needsFundingRoundsData?.data?.count || 0) +
    (expiredRoundsData?.data?.count || 0)
  const yellowOverlapCount = Math.max(0, yellowSubTotal - yellowTotal)

  return (
    <Box sx={{ p: 2, width: '80vw', margin: '0 auto', maxWidth: '1200px' }}>
      <Typography variant="h4" sx={{ mb: 2, textAlign: 'center' }}>
        Dashboard
      </Typography>

      <Box sx={{ mb: 3 }}>
        <OrgActivityMatrix />
      </Box>

      <DatabaseStats />

      {/* Summary cards row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-around',
          bgcolor: '#F2F2F2',
          p: 2,
          borderRadius: 1,
        }}
      >
        {/* Funders card */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexDirection: 'column',
            alignItems: 'center',
            bgcolor: 'background.paper',
            p: 2,
            borderRadius: 1,
            width: '300px',
          }}
        >
          <Typography variant="h5" fontWeight={600}>
            Total Funders
          </Typography>
          <Link
            component={RouterLink}
            to="/funders"
            variant="h5"
            fontWeight={600}
            sx={{
              textDecoration: 'none',
              color: 'inherit',
              textAlign: 'center',
              '&:hover': { textDecoration: 'underline', cursor: 'pointer' },
            }}
          >
            {totalFundersData?.data?.count || 0}
          </Link>

          <Tooltip
            title="Funders with the active flag set to true"
            placement="top"
          >
            <Typography variant="body1">
              Marked Active:{' '}
              <Link
                component={RouterLink}
                to="/funders?active=true"
                sx={linkSx}
              >
                {activeFundersData?.data?.count || 0}
              </Link>
            </Typography>
          </Tooltip>

          <Tooltip
            title="Funders with the active flag set to false — not shown to customers"
            placement="top"
          >
            <Typography variant="body1">
              Marked Inactive:{' '}
              <Link
                component={RouterLink}
                to="/funders?active=false"
                sx={linkSx}
              >
                {inactiveFundersData?.data?.count || 0}
              </Link>
            </Typography>
          </Tooltip>

          <Tooltip
            title="Funders with at least one green (customer-visible) opportunity"
            placement="top"
          >
            <Typography variant="body1">
              <FiberManualRecordIcon
                sx={{
                  color: '#4caf50',
                  fontSize: 16,
                  verticalAlign: 'middle',
                  mr: 0.5,
                }}
              />
              Has Live Opportunities:{' '}
              <Link
                component={RouterLink}
                to="/funders?opps_status=green"
                sx={linkSx}
              >
                {greenFundersData?.data?.count || 0}
              </Link>
            </Typography>
          </Tooltip>

          <Tooltip
            title="Funders that are active and have opportunities, but none are currently green (customer-visible)"
            placement="top"
          >
            <Typography variant="body1">
              <FiberManualRecordIcon
                sx={{
                  color: '#ff9800',
                  fontSize: 16,
                  verticalAlign: 'middle',
                  mr: 0.5,
                }}
              />
              Active, no Live Opportunites:{' '}
              <Link
                component={RouterLink}
                to="/funders?opps_status=yellow"
                sx={linkSx}
              >
                {yellowFundersData?.data?.count || 0}
              </Link>
            </Typography>
          </Tooltip>
        </Box>

        {/* Opportunities card */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexDirection: 'column',
            justifyContent: 'space-evenly',
            alignItems: 'center',
            bgcolor: 'background.paper',
            p: 2,
            borderRadius: 1,
            width: '300px',
          }}
        >
          <Typography variant="h5" fontWeight={600}>
            Total Opportunities
          </Typography>
          <Link
            component={RouterLink}
            to="/opportunities"
            variant="h5"
            fontWeight={600}
            sx={{
              textDecoration: 'none',
              color: 'inherit',
              textAlign: 'center',
              '&:hover': { textDecoration: 'underline', cursor: 'pointer' },
            }}
          >
            {totalOpportunitiesData?.data?.count || 0}
          </Link>

          <Tooltip
            title="Visible to customers: Active flag, published profile, funder active, and has current/future funding rounds or is always open"
            placement="top"
          >
            <Typography variant="body1">
              <FiberManualRecordIcon
                sx={{
                  color: '#4caf50',
                  fontSize: 16,
                  verticalAlign: 'middle',
                  mr: 0.5,
                }}
              />
              Green:{' '}
              <Link
                component={RouterLink}
                to="/opportunities?traffic_light_status=green"
                sx={linkSx}
              >
                {greenOpportunitiesData?.data?.count || 0}
              </Link>
            </Typography>
          </Tooltip>

          <Tooltip
            title="Not yet visible to customers: Flagged as Active but missing profile or funding rounds, or is flagged Needs Work"
            placement="top"
          >
            <Typography variant="body1">
              <FiberManualRecordIcon
                sx={{
                  color: '#ff9800',
                  fontSize: 16,
                  verticalAlign: 'middle',
                  mr: 0.5,
                }}
              />
              Yellow:{' '}
              <Link
                component={RouterLink}
                to="/opportunities?traffic_light_status=yellow"
                sx={linkSx}
              >
                {yellowOpportunitiesData?.data?.count || 0}
              </Link>
            </Typography>
          </Tooltip>

          <Tooltip
            title="Inactive: flag set to Inactive, or funder is inactive"
            placement="top"
          >
            <Typography variant="body1">
              <FiberManualRecordIcon
                sx={{
                  color: '#f44336',
                  fontSize: 16,
                  verticalAlign: 'middle',
                  mr: 0.5,
                }}
              />
              Red:{' '}
              <Link
                component={RouterLink}
                to="/opportunities?traffic_light_status=red"
                sx={linkSx}
              >
                {redOpportunitiesData?.data?.count || 0}
              </Link>
            </Typography>
          </Tooltip>

          <Tooltip
            title="Opportunities with flag set to 'Needs Work' — a subset of yellow"
            placement="top"
          >
            <Typography variant="body1" sx={{ fontWeight: '600' }}>
              Flagged Needs Work:{' '}
              <Link
                component={RouterLink}
                to="/opportunities?flag_for_updates=Needs%20Work"
                sx={linkSx}
              >
                {needsWorkOpportunitiesData?.data?.count || 0}
              </Link>
            </Typography>
          </Tooltip>
        </Box>
      </Box>

      {/* Green / Yellow detail row */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-around',
          mt: 2,
          bgcolor: '#F2F2F2',
          p: 2,
          borderRadius: 1,
        }}
      >
        <Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
          {/* Green Opportunities */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
              p: 2,
              borderRadius: 1,
              width: '50%',
              alignItems: 'center',
              minHeight: '300px',
              justifyContent: 'flex-start',
            }}
          >
            <Tooltip
              title="Visible to customers: Flagged as Active, profile published, funder active, and has current/future funding rounds or is always open"
              placement="top"
            >
              <Typography
                variant="h5"
                sx={{ textAlign: 'center', fontWeight: '600' }}
              >
                <FiberManualRecordIcon
                  sx={{
                    color: '#4caf50',
                    fontSize: 20,
                    verticalAlign: 'middle',
                    mr: 0.5,
                  }}
                />
                Live Opportunities
              </Typography>
            </Tooltip>
            <Link
              component={RouterLink}
              to="/opportunities?traffic_light_status=green"
              variant="h5"
              sx={{
                textAlign: 'center',
                fontWeight: '600',
                mb: 2,
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': { textDecoration: 'underline', cursor: 'pointer' },
              }}
            >
              {greenOpportunitiesCount} ({greenOpportunitiesPercentage}%)
            </Link>
            <Typography
              variant="caption"
              sx={{ textAlign: 'center', width: '80%', mb: 4 }}
            >
              Visible to customers — Flagged as Active, profile published,
              funder active, and always open or has current/future funding
              rounds.
            </Typography>

            <Divider sx={{ width: '100%', my: 1 }} />

            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Tooltip
                title="Green opportunities marked as always open — available to apply at any time"
                placement="top"
              >
                <Typography variant="body1" sx={{ fontWeight: '600' }}>
                  Always Open
                </Typography>
              </Tooltip>
              <Typography variant="caption">
                Green opportunities marked as always open — permanently
                available
              </Typography>
              <Link
                component={RouterLink}
                to="/opportunities?traffic_light_status=green&always_open=true"
                variant="body1"
                sx={bigLinkSx}
              >
                {alwaysOpenGreenData?.data?.count || 0}
              </Link>
            </Box>

            <Divider sx={{ width: '100%', mb: 1 }} />

            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Tooltip
                title="Green opportunities with a funding round open right now (not always open)"
                placement="top"
              >
                <Typography variant="body1" sx={{ fontWeight: '600' }}>
                  Currently Open Rounds
                </Typography>
              </Tooltip>
              <Typography variant="caption">
                Green opportunities with a round open now (excludes always open)
              </Typography>
              <Link
                component={RouterLink}
                to="/opportunities?has_current_funding_rounds=true&traffic_light_status=green&always_open=false"
                variant="body1"
                sx={bigLinkSx}
              >
                {currentFundingRoundsData?.data?.count || 0}
              </Link>
            </Box>

            <Divider sx={{ width: '100%', mb: 1 }} />

            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Tooltip
                title="Green opportunities with a funding round opening in the next 30 days"
                placement="top"
              >
                <Typography variant="body1" sx={{ fontWeight: '600' }}>
                  Upcoming Funding Rounds
                </Typography>
              </Tooltip>
              <Typography variant="caption">
                Green opportunities with upcoming Funding Rounds (next month)
              </Typography>
              <Link
                component={RouterLink}
                to={`/opportunities?traffic_light_status=green&funding_start_date=${startDate}&funding_end_date=${endDate}`}
                variant="body1"
                sx={bigLinkSx}
              >
                {upcomingFundingRoundsData?.data?.count || 0}
              </Link>
            </Box>

            <Divider sx={{ width: '100%', mb: 1 }} />

            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Tooltip
                title="Green opportunities with a funding round opening more than 30 days away — not yet upcoming"
                placement="top"
              >
                <Typography variant="body1" sx={{ fontWeight: '600' }}>
                  Future Rounds
                </Typography>
              </Tooltip>
              <Typography variant="caption">
                Green opportunities with rounds beyond the next 30 days
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: '600', mt: 0.5 }}>
                {futureRoundsCount}
              </Typography>
            </Box>
          </Box>

          {/* Yellow Opportunities */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
              p: 2,
              borderRadius: 1,
              width: '50%',
              alignItems: 'center',
              minHeight: '300px',
              justifyContent: 'flex-start',
            }}
          >
            <Tooltip
              title="Not yet visible to customers: Flagged as Active but missing profile or funding rounds, or flagged Needs Work"
              placement="top"
            >
              <Typography
                variant="h5"
                sx={{ textAlign: 'center', fontWeight: '600' }}
              >
                <FiberManualRecordIcon
                  sx={{
                    color: '#ff9800',
                    fontSize: 20,
                    verticalAlign: 'middle',
                    mr: 0.5,
                  }}
                />
                Not Available
              </Typography>
            </Tooltip>
            <Link
              component={RouterLink}
              to="/opportunities?traffic_light_status=yellow"
              variant="h5"
              sx={{
                textAlign: 'center',
                fontWeight: '600',
                mb: 2,
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': { textDecoration: 'underline', cursor: 'pointer' },
              }}
            >
              {yellowTotal} ({yellowOpportunitiesPercentage}%)
            </Link>
            <Typography
              variant="caption"
              sx={{ textAlign: 'center', width: '80%', mb: 4 }}
            >
              Not yet visible to customers — flagged Needs Work, or flagged
              Active with no profile or with no current rounds (none recorded,
              or all expired).
            </Typography>
            <Divider sx={{ width: '100%', my: 1 }} />

            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Tooltip
                title="Opportunities with flag set to 'Needs Work' — explicitly flagged for attention"
                placement="top"
              >
                <Typography variant="body1" sx={{ fontWeight: '600' }}>
                  Flagged Needs Work
                </Typography>
              </Tooltip>
              <Typography variant="caption">
                Explicitly flagged for attention — need review before they can
                become green
              </Typography>
              <Link
                component={RouterLink}
                to="/opportunities?flag_for_updates=Needs%20Work"
                variant="body1"
                sx={bigLinkSx}
              >
                {needsWorkOpportunitiesData?.data?.count || 0}
              </Link>

              <Divider sx={{ width: '100%', my: 1 }} />

              <Tooltip
                title="Flagged as Active with no profile published — cannot become green until a profile is published"
                placement="top"
              >
                <Typography variant="body1" sx={{ fontWeight: '600' }}>
                  No Profile
                </Typography>
              </Tooltip>
              <Typography variant="caption">
                Flagged as Active with no profile — need a profile before they
                can become green
              </Typography>
              <Link
                component={RouterLink}
                to="/opportunities?flag_for_updates=Active&profile=false"
                variant="body1"
                sx={bigLinkSx}
              >
                {activeNoProfileData?.data?.count || 0}
              </Link>

              <Divider sx={{ width: '100%', my: 1 }} />

              <Tooltip
                title="Flagged as Active with no location for the opportunity or its funder"
                placement="top"
              >
                <Typography variant="body1" sx={{ fontWeight: '600' }}>
                  No Locations
                </Typography>
              </Tooltip>
              <Typography variant="caption">
                Flagged as Active with no location for the opportunity or funder
              </Typography>
              <Link
                component={RouterLink}
                to="/opportunities?current=1&region_id=%7B%22value%22%3A%7B%22id%22%3A%22no_region%22%7D%2C%22label%22%3A%22None%22%7D&pageSize=25&sorters%5B0%5D%5Bfield%5D=next_check_in_date&sorters%5B0%5D%5Border%5D=desc&inherit_regions_from_funder=true&flag_for_updates=Active"
                variant="body1"
                sx={bigLinkSx}
              >
                {needsRegionsData?.data?.count || 0}
              </Link>

              <Divider sx={{ width: '100%', my: 1 }} />

              <Tooltip
                title="Flagged as Active that are not always open and have no funding rounds on record"
                placement="top"
              >
                <Typography variant="body1" sx={{ fontWeight: '600' }}>
                  No Funding Rounds
                </Typography>
              </Tooltip>
              <Typography variant="caption">
                Flagged as Active, not always open, and no funding rounds on
                record at all
              </Typography>
              <Link
                component={RouterLink}
                to="/opportunities?needs_funding_rounds=true"
                variant="body1"
                sx={bigLinkSx}
              >
                {needsFundingRoundsData?.data?.count || 0}
              </Link>

              <Divider sx={{ width: '100%', my: 1 }} />

              <Tooltip
                title="Flagged as Active where all funding rounds have expired — previously green, now waiting for new rounds"
                placement="top"
              >
                <Typography variant="body1" sx={{ fontWeight: '600' }}>
                  Expired Rounds
                </Typography>
              </Tooltip>
              <Typography variant="caption">
                Flagged as Active, has profile, but all funding rounds are
                closed — need new rounds to become green again
              </Typography>
              <Link
                component={RouterLink}
                to="/opportunities?has_expired_rounds=true"
                variant="body1"
                sx={bigLinkSx}
              >
                {expiredRoundsData?.data?.count || 0}
              </Link>

              {yellowOverlapCount > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    mt: 1,
                    color: 'text.secondary',
                  }}
                >
                  {yellowOverlapCount}{' '}
                  {yellowOverlapCount === 1
                    ? 'opportunity is'
                    : 'opportunities are'}{' '}
                  counted in multiple categories above
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <OppTagStats />
    </Box>
  )
}
